/**
 * The data contract both offer cards map into.
 *
 * Deliberately structural rather than a GraphQL type: admin reads
 * `OrderQuoteOfferBatch` and the client reads `ClientOrderQuoteOfferBatch`, and
 * those two shapes will keep diverging. Each app flattens its own offer into
 * this, which is also where per-audience wording is decided — see
 * `treatmentNote`.
 */

/** Raw enum values, as strings, so this package needn't depend on the schema. */
export const NO_SAMPLE = 'NO_SAMPLE';
export const SAMPLE_NO_SHIPPING = 'SAMPLE_NO_SHIPPING';
export const SAMPLE_WITH_SHIPPING = 'SAMPLE_WITH_SHIPPING';

export interface ExtrasSample {
	id: string;
	/** The quoted total — sample + shipping + fee. */
	totalPrice: number | null;
	minProductionDays: number | null;
	maxProductionDays: number | null;
	deliveryDaysMin: number | null;
	deliveryDaysMax: number | null;
	notes?: string | null;
	/**
	 * Already-worded payment-treatment note, or null for the ordinary CHARGED
	 * case. Passed in rather than derived: admin says "Deducted from order" and
	 * the client says "Deducted from your order", and that difference is the
	 * point — the client is the one being deducted.
	 */
	treatmentNote?: string | null;
	/**
	 * What the total is made of — the sample's own price and its freight.
	 * Admin-only: the client is quoted one figure per choice, not its parts.
	 */
	breakdown?: Array<{ label: string; amount: number | null }> | null;
	/**
	 * One total per choice, so each option prices itself. Client-only: admin
	 * authors the sample rather than choosing between the ways of taking it.
	 */
	pricesByChoice?: Array<{ sampleChoice: string; totalPrice: number }> | null;
}

export interface ExtrasAdjustment {
	id: string;
	name: string;
	description?: string | null;
	/** True for a fee, false for a discount. */
	isFee: boolean;
	/** True when `value` is a percentage rather than an amount. */
	isPercentage: boolean;
	value: number;
}

export interface ExtrasAttachment {
	id: string;
	name: string;
	/** What the file is and why it is there, written for the client to read.
	 * Null on files uploaded before descriptions existed, and on any put up bare. */
	description?: string | null;
	mimeType: string;
	size: number;
	url?: string | null;
}

/** One product's worth of extras, named as the reader will see it. */
export interface ExtrasProduct {
	offerId: string;
	/**
	 * The quote line behind the offer. Two offers on the same product are two
	 * entries here with two different `offerId`s and the SAME `itemId` — which is
	 * how the attachments section knows not to list that product's files twice,
	 * since both offers serve the one list hanging off the line.
	 */
	itemId?: string;
	name: string;
	/**
	 * Taken out of the order being priced. Its rows stay — admin still has to be
	 * able to author a sample on a product it has deselected — but they read as
	 * excluded and nothing on them can be picked, because none of it counts.
	 *
	 * A caller that drops excluded products from the list entirely (the client
	 * does) never sets this.
	 */
	excluded?: boolean;
	sample?: ExtrasSample | null;
	adjustments: ExtrasAdjustment[];
	attachments: ExtrasAttachment[];
}

/** Renders an amount in the reader's display currency. Each app has its own,
 * bound to its own currency store. */
export type CurrencyTextComponent = React.ComponentType<{
	amount: number | null | undefined;
	className?: string;
}>;

/** The in-app file viewer. Identical in both apps today, but it belongs to them,
 * not here — it pulls in PDF rendering and image cropping. */
export type FilePreviewComponent = React.ComponentType<{
	files: Array<{ id: string; url: string; name: string; mimeType: string; size: number }>;
	initialIndex: number;
	onClose: () => void;
}>;
